var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { AsciiText } from './framework/AsciiText';
import { CommandSocketInitializer } from './framework/CommandSocketInitializer';
const debug = true;
const allowedMuteTypes = ['input', 'output', 'crossPoint'];
const allowedVolumeTypes = ['input', 'output', 'crossPoint'];
const allowedTypes = {
    amplifier: allowedVolumeTypes,
    assistiveListeningMute: allowedMuteTypes,
    assistiveListeningMuteStatus: allowedMuteTypes,
    assistiveListeningVolume: allowedVolumeTypes,
    audioInputMute: allowedMuteTypes,
    audioInputMuteStatus: allowedMuteTypes,
    audioInputVolume: allowedVolumeTypes,
    mics: allowedMuteTypes,
    micsMuteStatus: allowedMuteTypes,
    overflowRoomMute: allowedMuteTypes,
    overflowRoomMuteStatus: allowedMuteTypes,
    overflowRoomVolume: allowedVolumeTypes,
    preset: ['preset'],
    receiveGain: [],
    soundReinforcementMute: allowedMuteTypes,
    soundReinforcementMuteStatus: allowedMuteTypes,
    soundReinforcementVolume: allowedVolumeTypes,
    telephony: [],
};
function isAshlyAudioChannelConfig(config) {
    return typeof config === 'string';
}
function isAshlyPresetMuteConfig(config) {
    return (typeof config === 'object' &&
        'mutePreset' in config &&
        typeof config.mutePreset === 'string' &&
        'muteStatus' in config &&
        typeof config.muteStatus === 'string' &&
        'unmutePreset' in config &&
        typeof config.unmutePreset === 'string');
}
function makeCommand(data) {
    const output = [];
    output.push(0xf0, 0x00, 0x01, 0x2a, 0x06, 0x00); // command header
    output.push(...data);
    return AsciiText.decode(output);
}
class Channel {
    constructor(type, key, log) {
        this.type = type;
        this.key = key;
        this.log = log;
        this.chanNumber = 0;
        this.isMuteConfigured = false;
        this.isVolumeConfigured = false;
        this.name = '';
        this.inputNum = 0; // used with crosspoints
        this.outputNum = 0; // used with crosspoints
    }
    detectName(socket, tags) {
        return __awaiter(this, void 0, void 0, function* () { });
    }
    getFader(socket, tags) {
        return __awaiter(this, void 0, void 0, function* () {
            throw new Error(`fader not supported for ${this.key}`);
        });
    }
    getMute(socket, tags) {
        return __awaiter(this, void 0, void 0, function* () {
            throw new Error(`mute not supported for ${this.key}`);
        });
    }
    getMuteAndVolume(socket, tags) {
        return __awaiter(this, void 0, void 0, function* () {
            throw new Error(`getMuteAndVolume not supported for ${this.key}`);
        });
    }
    loadPreset(socket, tags) {
        return __awaiter(this, void 0, void 0, function* () {
            throw new Error(`preset not supported for ${this.key}`);
        });
    }
    setFader(socket, value, tags) {
        return __awaiter(this, void 0, void 0, function* () {
            throw new Error(`fader not supported for ${this.key}`);
        });
    }
    setMute(socket, value, tags) {
        return __awaiter(this, void 0, void 0, function* () {
            throw new Error(`mute not supported for ${this.key}`);
        });
    }
    isAllowed(filter) {
        return allowedTypes[filter].includes(this.type);
    }
    matches(key) {
        return this.key === key;
    }
}
class Channels {
    constructor(log) {
        this.log = log;
        this.channels = [];
    }
    addChannel(chan) {
        this.channels.push(chan);
    }
    get areAnyConfigured() {
        for (const channel of this.channels) {
            if (channel.isMuteConfigured || channel.isVolumeConfigured)
                return true;
        }
        return false;
    }
    computeFilters(type) {
        return {
            amplifier: allowedTypes.amplifier.includes(type),
            assistiveListeningMute: allowedTypes.assistiveListeningMute.includes(type),
            assistiveListeningMuteStatus: allowedTypes.assistiveListeningMuteStatus.includes(type),
            assistiveListeningVolume: allowedTypes.assistiveListeningVolume.includes(type),
            audioInputMute: allowedTypes.audioInputMute.includes(type),
            audioInputMuteStatus: allowedTypes.audioInputMuteStatus.includes(type),
            audioInputVolume: allowedTypes.audioInputVolume.includes(type),
            mics: allowedTypes.mics.includes(type),
            micsMuteStatus: allowedTypes.micsMuteStatus.includes(type),
            overflowRoomMute: allowedTypes.overflowRoomMute.includes(type),
            overflowRoomMuteStatus: allowedTypes.overflowRoomMuteStatus.includes(type),
            overflowRoomVolume: allowedTypes.overflowRoomVolume.includes(type),
            preset: type === 'preset',
            receiveGain: false,
            soundReinforcementMute: allowedTypes.soundReinforcementMute.includes(type),
            soundReinforcementMuteStatus: allowedTypes.soundReinforcementMuteStatus.includes(type),
            soundReinforcementVolume: allowedTypes.soundReinforcementVolume.includes(type),
            telephony: false,
        };
    }
    getAllConfiguredChannels() {
        return this.channels.filter(channel => channel.isMuteConfigured || channel.isVolumeConfigured);
    }
    getByKey(key) {
        return this.channels.find(channel => channel.key === key);
    }
    getChannel(id) {
        for (const channel of this.channels) {
            if (channel.matches(id))
                return channel;
        }
        return undefined;
    }
    getChannelType(id) {
        for (const channel of this.channels) {
            if (channel.matches(id))
                return channel === null || channel === void 0 ? void 0 : channel.type;
        }
        return undefined;
    }
    getConfiguredChannels(control) {
        return this.channels.filter(channel => control === 'mute' ? channel.isMuteConfigured : channel.isVolumeConfigured);
    }
    getDetected() {
        const detectedChannels = [];
        for (const channel of this.channels) {
            const detectedChannel = {
                channel: channel.key,
                type: channel.type,
                name: channel.name,
                filters: this.computeFilters(channel.type),
            };
            if (channel.name)
                detectedChannel.name = channel.name;
            detectedChannels.push(detectedChannel);
        }
        return detectedChannels;
    }
    // Get names from the device and update the channel names.
    updateNames(socket) {
        var _a, _b, _c, _d;
        return __awaiter(this, void 0, void 0, function* () {
            const presetNames = [];
            const presetNameResp = yield socket.send(makeCommand([0x04]), /\xf0\x00\x01\x2a\x06\x00\x05([\s\S]{700})/, ['presetNames']);
            // There are 35 names with 25 characters each (= 700 bytes).
            for (let i = 0; i < 35; i++) {
                const name = presetNameResp[1].substring(i * 20, (i + 1) * 20).trim();
                presetNames.push(name);
            }
            // Detect the names of input and output channels and set the preset names.
            for (const chan of Object.values(this.channels)) {
                if (chan.type === 'preset') {
                    const chanNum = chan.chanNumber;
                    const presetName = presetNames[chanNum];
                    if (debug)
                        this.log.debug(`presetname for chan: ${chanNum} is ${presetName}`);
                    if (presetName)
                        chan.name = presetName;
                }
                else if (chan.type !== 'crossPoint') {
                    yield chan.detectName(socket, ['get.name']);
                }
            }
            // Update the crosspoint channel names to use the detected input and output names.
            for (const chan of Object.values(this.channels)) {
                if (chan.type === 'crossPoint') {
                    const inName = (_b = (_a = this.getByKey(`input.${chan.inputNum + 1}`)) === null || _a === void 0 ? void 0 : _a.name) !== null && _b !== void 0 ? _b : `Input ${chan.inputNum + 1}`;
                    const outName = (_d = (_c = this.getByKey(`output.${chan.outputNum + 1}`)) === null || _c === void 0 ? void 0 : _c.name) !== null && _d !== void 0 ? _d : `Output ${chan.outputNum + 1}`;
                    chan.name = `${inName} to ${outName}`;
                }
            }
        });
    }
}
class InputChannel extends Channel {
    constructor(id, log) {
        super('input', `input.${id + 1}`, log);
        this.name = `Input ${id + 1}`;
        this.chanNumber = id; // zero-based indexing
    }
    detectName(socket, tags) {
        return __awaiter(this, void 0, void 0, function* () {
            const [command, regExp] = this.makeCommandRegExp([0x00, 0x01, this.chanNumber]);
            const resp = yield socket.send(command, regExp, tags);
            if (debug)
                this.log.debug(`detectName chanName for input ${this.chanNumber} = ${resp[1]}`);
            this.name = resp[1].trim();
        });
    }
    getMute(socket, tags) {
        return __awaiter(this, void 0, void 0, function* () {
            const [command, regExp] = this.makeCommandRegExp([0x00, 0x01, this.chanNumber]);
            const resp = yield socket.send(command, regExp, tags);
            return resp[2] === '\x01'; // 01 is muted, 00 is unmuted
        });
    }
    // Support getting mute and gains in the same response
    getMuteAndVolume(socket, tags) {
        return __awaiter(this, void 0, void 0, function* () {
            const [command, regExp] = this.makeCommandRegExp([0x00, 0x01, this.chanNumber]);
            const resp = yield socket.send(command, regExp, tags);
            const mute = resp[2] === '\x01';
            const gain = (resp[3].charCodeAt(0) << 7) + resp[3].charCodeAt(1);
            return [mute, gain];
        });
    }
    setMute(socket, value, tags) {
        return __awaiter(this, void 0, void 0, function* () {
            const [command, regExp] = this.makeCommandRegExp([
                0x15,
                this.chanNumber,
                value ? 0x01 : 0x00,
            ]);
            const resp = yield socket.send(command, regExp, tags);
            return resp[2] === '\x01'; // 01 is muted, 00 is unmuted
        });
    }
    getFader(socket, tags) {
        return __awaiter(this, void 0, void 0, function* () {
            // 00 data request for 01 input channel settings
            const [command, regExp] = this.makeCommandRegExp([0x00, 0x01, this.chanNumber]);
            const resp = yield socket.send(command, regExp, tags);
            const gain = (resp[3].charCodeAt(0) << 7) + resp[3].charCodeAt(1);
            if (debug)
                this.log.debug(`driver: inputChannel: getFader gain: ${gain}`);
            return gain;
        });
    }
    setFader(socket, value, tags) {
        return __awaiter(this, void 0, void 0, function* () {
            const loByte = value & 0x7f;
            const hiByte = (value >> 7) & 0x7f;
            const [command, regExp] = this.makeCommandRegExp([0x0c, this.chanNumber, hiByte, loByte]);
            const resp = yield socket.send(command, regExp, tags);
            const gain = (resp[3].charCodeAt(0) << 7) + resp[3].charCodeAt(1);
            if (debug)
                this.log.debug(`driver: inputChannel: setFader gain response: ${gain}`);
            return gain;
        });
    }
    makeCommandRegExp(data) {
        let regExpStr = '';
        regExpStr = `\\xf0\\x00\\x01\\x2a\\x06\\x00\\x01`; // header and message type as response
        regExpStr += '\\x01'; // input channel type
        regExpStr += `\\x${this.chanNumber.toString(16).padStart(2, '0')}`; // channel number, zero-based
        regExpStr += `([\\s\\S]{20})`; // channel name
        regExpStr += `([\\s\\S])`; // mute status
        regExpStr += `[\\s\\S]{3}`; // skip eq status, preamp gain, phantom power
        regExpStr += `([\\s\\S]{2})`; // gain high and low bytes
        regExpStr += `[\\s\\S]+`; // the rest
        return [makeCommand(data), new RegExp(regExpStr)];
    }
}
class OutputChannel extends Channel {
    constructor(id, log) {
        super('output', `output.${id + 1}`, log);
        this.name = `Output ${id + 1}`;
        this.chanNumber = id;
    }
    detectName(socket, tags) {
        return __awaiter(this, void 0, void 0, function* () {
            const [command, regExp] = this.makeCommandRegExp([0x00, 0x02, this.chanNumber]);
            const resp = yield socket.send(command, regExp, tags);
            if (debug)
                this.log.debug(`detectName chanName for output ${this.chanNumber} = ${resp[1]}`);
            this.name = resp[1].trim();
        });
    }
    getMute(socket, tags) {
        return __awaiter(this, void 0, void 0, function* () {
            // 00 data request for 02 output channel settings
            const [command, regExp] = this.makeCommandRegExp([0x00, 0x02, this.chanNumber]);
            const resp = yield socket.send(command, regExp, tags);
            return resp[2] === '\x01'; // 01 is muted, 00 is unmuted
        });
    }
    getMuteAndVolume(socket, tags) {
        return __awaiter(this, void 0, void 0, function* () {
            const [command, regExp] = this.makeCommandRegExp([0x00, 0x02, this.chanNumber]);
            const resp = yield socket.send(command, regExp, tags);
            const mute = resp[2] === '\x01';
            const gain = (resp[3].charCodeAt(0) << 7) + resp[3].charCodeAt(1);
            return [mute, gain];
        });
    }
    setMute(socket, value, tags) {
        return __awaiter(this, void 0, void 0, function* () {
            const [command, regExp] = this.makeCommandRegExp([
                0x15,
                this.chanNumber + 0x40,
                value ? 0x01 : 0x00,
            ]);
            const resp = yield socket.send(command, regExp, tags);
            return resp[2] === '\x01'; // 01 is muted, 00 is unmuted
        });
    }
    getFader(socket, tags) {
        return __awaiter(this, void 0, void 0, function* () {
            // 00 data request for 02 output channel settings
            const [command, regExp] = this.makeCommandRegExp([0x00, 0x02, this.chanNumber]);
            const resp = yield socket.send(command, regExp, tags);
            const gain = (resp[3].charCodeAt(0) << 7) + resp[3].charCodeAt(1);
            if (debug) {
                this.log.debug(`driver: outputChannel: getFader gain: ${gain}, hi:${resp[3].charCodeAt(0)}, lo: ${resp[3].charCodeAt(1)}`);
            }
            return gain;
        });
    }
    setFader(socket, value, tags) {
        return __awaiter(this, void 0, void 0, function* () {
            const loByte = value & 0x7f;
            const hiByte = (value >> 7) & 0x7f;
            // output channels start at 0x40
            const [command, regExp] = this.makeCommandRegExp([
                0x0c,
                this.chanNumber + 0x40,
                hiByte,
                loByte,
            ]);
            const resp = yield socket.send(command, regExp, tags);
            const gain = (resp[3].charCodeAt(0) << 7) + resp[3].charCodeAt(1);
            if (debug)
                this.log.debug(`driver: OutputChannel: setFader gain response: ${gain}`);
            return gain;
        });
    }
    makeCommandRegExp(data) {
        let regExpStr = '';
        regExpStr = `\\xf0\\x00\\x01\\x2a\\x06\\x00\\x01`; // header and message type as response
        regExpStr += '\\x02'; // output channel type
        regExpStr += `\\x${this.chanNumber.toString(16).padStart(2, '0')}`; // output number
        regExpStr += `([\\s\\S]{20})`; // channel name
        regExpStr += `([\\s\\S])`; // output mute status
        regExpStr += `[\\s\\S]{4}`; // skip equ and mix status
        regExpStr += `[\\s\\S]{20}`; // skip mix status
        // up to position 54
        regExpStr += `[\\s\\S]{116}`;
        // up to position 170
        regExpStr += `([\\s\\S]{2})`; // gain high and low bytes
        regExpStr += `[\\s\\S]{7}`; // the rest
        return [makeCommand(data), new RegExp(regExpStr)];
    }
}
// Crosspoints are the control points between an input and a specific mixer output.
class CrossPointChannel extends Channel {
    constructor(inputNum, outputNum, log) {
        super('crossPoint', `input.${inputNum + 1}.output.${outputNum + 1}`, log);
        this.inputNum = inputNum;
        this.outputNum = outputNum;
        this.name = `Input ${inputNum + 1} to Output ${outputNum + 1}`;
    }
    getMute(socket, tags) {
        return __awaiter(this, void 0, void 0, function* () {
            // 00 data request for 02 output channel settings
            const [command, regExp] = this.makeCommandRegExp([0x00, 0x02, this.outputNum]);
            const resp = yield socket.send(command, regExp, tags);
            const mute = ((resp[3].charCodeAt(this.inputNum) >> 6) & 0x01) === 0x01;
            if (debug) {
                this.log.debug(`driver: Crosspoint in ${this.inputNum} to out ${this.outputNum}: mute = ${mute}`);
            }
            return mute;
        });
    }
    getMuteAndVolume(socket, tags) {
        return __awaiter(this, void 0, void 0, function* () {
            const [command, regExp] = this.makeCommandRegExp([0x00, 0x02, this.outputNum]);
            const resp = yield socket.send(command, regExp, tags);
            const mute = ((resp[3].charCodeAt(this.inputNum) >> 6) & 0x01) === 0x01;
            const gain = resp[3].charCodeAt(this.inputNum) & 0x3f;
            return [mute, gain];
        });
    }
    setMute(socket, value, tags) {
        return __awaiter(this, void 0, void 0, function* () {
            const [command, regExp] = this.makeCommandRegExp([
                0x19,
                this.outputNum + 0x40,
                this.inputNum,
                value ? 0x01 : 0x00,
            ]);
            const resp = yield socket.send(command, regExp, tags);
            const muteValue = ((resp[3].charCodeAt(this.inputNum) >> 6) & 0x01) === 0x01;
            if (debug) {
                this.log.debug(`driver: set Crosspoint in ${this.inputNum + 1} to out ${this.outputNum + 1}: muteValue = ${muteValue}`);
            }
            return muteValue;
        });
    }
    getFader(socket, tags) {
        return __awaiter(this, void 0, void 0, function* () {
            // 00 data request for 02 output channel settings
            const [command, regExp] = this.makeCommandRegExp([0x00, 0x02, this.outputNum]);
            const resp = yield socket.send(command, regExp, tags);
            const gain = resp[3].charCodeAt(this.inputNum) & 0x3f;
            if (debug) {
                this.log.debug(`driver: get Crosspoint gain in ${this.inputNum} to out ${this.outputNum}: gain = ${gain}`);
            }
            return gain;
        });
    }
    setFader(socket, value, tags) {
        return __awaiter(this, void 0, void 0, function* () {
            const [command, regExp] = this.makeCommandRegExp([
                0x12,
                this.outputNum + 0x40,
                this.inputNum,
                value,
                0x01,
                0x00, //unmute if setting the gain
            ]);
            const resp = yield socket.send(command, regExp, tags);
            const gain = resp[3].charCodeAt(this.inputNum) & 0x3f;
            if (debug) {
                this.log.debug(`driver: set Crosspoint gain in ${this.inputNum} to out ${this.outputNum}: gain = ${gain}`);
            }
            return gain;
        });
    }
    makeCommandRegExp(data) {
        let regExpStr = '';
        regExpStr = `\\xf0\\x00\\x01\\x2a\\x06\\x00\\x01`; // header and message type as response
        regExpStr += '\\x02'; // output channel type
        regExpStr += `\\x${this.outputNum.toString(16).padStart(2, '0')}`; // output number
        regExpStr += `([\\s\\S]{20})`; // channel name
        regExpStr += `([\\s\\S])`; // output mute status
        regExpStr += `[\\s\\S]{4}`; // skip equ and mix status
        regExpStr += `([\\s\\S]{20})`; // mix gains and mutes
        // up to position 54
        regExpStr += `[\\s\\S]{116}`;
        // up to position 170
        regExpStr += `[\\s\\S]{2}`; // channel gain high and low bytes
        regExpStr += `[\\s\\S]{7}`; // the rest
        return [makeCommand(data), new RegExp(regExpStr)];
    }
}
class PresetChannel extends Channel {
    constructor(id, log) {
        super('preset', `preset.${id + 1}`, log);
        this.name = `Preset ${id + 1}`;
        this.chanNumber = id;
    }
    loadPreset(socket, tags) {
        return __awaiter(this, void 0, void 0, function* () {
            // There is no response from the preset recall command.
            yield socket.sendAsync(makeCommand([0x07, this.chanNumber, 0x00]), tags);
        });
    }
}
class AshlyChannels extends Channels {
    constructor(numInputs, numOutputs, numPresets, log) {
        super(log);
        for (let inp = 0; inp < numInputs; inp++) {
            this.channels.push(new InputChannel(inp, log));
        }
        for (let outp = 0; outp < numOutputs; outp++) {
            this.channels.push(new OutputChannel(outp, log));
        }
        // Only create submix crosspoints for the number of inputs defined instead of creating
        // the maximum of 20 crosspoints for each output in each submix.
        for (let inp = 0; inp < numInputs; inp++) {
            for (let outp = 0; outp < numOutputs; outp++) {
                this.channels.push(new CrossPointChannel(inp, outp, log));
            }
        }
        for (let i = 0; i < numPresets; i++) {
            this.channels.push(new PresetChannel(i, log));
        }
    }
}
export class AshlyNeDsp {
    constructor(socket, state, log, framework) {
        this.socket = socket;
        this.state = state;
        this.log = log;
        this.configuredChannels = [];
        this.channelPollIndex = 0;
        // controls
        this.detected = {
            get: (tags) => __awaiter(this, void 0, void 0, function* () {
                return this.channels.getDetected();
            }),
        };
        this.mute = {
            get: (key, tags) => __awaiter(this, void 0, void 0, function* () {
                const channel = this.channels.getByKey(key);
                if (channel === undefined)
                    throw new Error(`no channel for key = ${key}`);
                return yield channel.getMute(this.socket, tags);
            }),
            set: (key, value, tags) => __awaiter(this, void 0, void 0, function* () {
                const channel = this.channels.getByKey(key);
                if (channel === undefined)
                    throw new Error(`no channel for key = ${key}`);
                if (channel.presetMute === undefined) {
                    const retValue = yield channel.setMute(this.socket, value, tags);
                    return retValue !== null && retValue !== void 0 ? retValue : this.state.mutes.values[key];
                }
                else {
                    if (value) {
                        yield channel.presetMute.mute.loadPreset(this.socket, tags);
                    }
                    else {
                        yield channel.presetMute.unmute.loadPreset(this.socket, tags);
                    }
                    return value;
                }
            }),
            getKey: this.getKey.bind(this),
            validate: this.validate.bind(this), // happens before constructor binding
        };
        this.volume = {
            get: (key, tags) => __awaiter(this, void 0, void 0, function* () {
                const channel = this.channels.getByKey(key);
                if (channel === undefined)
                    throw new Error(`no channel for key = ${key}`);
                return yield channel.getFader(this.socket, tags);
            }),
            set: (key, value, tags) => __awaiter(this, void 0, void 0, function* () {
                const channel = this.channels.getByKey(key);
                if (channel === undefined)
                    throw new Error(`no channel for key = ${key}`);
                yield this.syncVolumeMute(channel, value);
                if (debug)
                    this.log.debug(`driver: set volume: key: ${key}, volume: ${value}`);
                return yield channel.setFader(this.socket, value, tags);
            }),
            getKey: this.getKey.bind(this),
            range: (config, info) => __awaiter(this, void 0, void 0, function* () {
                if (debug) {
                    this.log.debug(`range: config = ${JSON.stringify(config)}, info = ${JSON.stringify(info)}  `);
                }
                const isXp = this.channels.getChannelType(config) === 'crossPoint';
                if (isXp) {
                    // Crosspoints have a gain range from 0x01 to 0x3f for -50 to + 12dB
                    if (debug)
                        this.log.debug(`isXp in volume`);
                    if (info.volumeType === 'trim')
                        return { max: 0x3f, min: 0x27, round: 1 };
                    return { max: 0x3f, min: 0x0c, round: 1 }; // volume +12 to -38 dB
                }
                else {
                    if (info.volumeType === 'trim')
                        return { max: 8312, min: 8072, round: 1 };
                    return { max: 8312, min: 7812, round: 1 }; // volume +12 to -38 dB
                }
            }),
            validate: this.validate.bind(this), // happens before constructor binding
        };
        // polling
        this.polls = [
            {
                intervalMs: 4000,
                function: () => __awaiter(this, void 0, void 0, function* () {
                    if (this.configuredChannels.length > 0) {
                        const channel = this.configuredChannels[this.channelPollIndex];
                        if (channel !== undefined) {
                            const [mute, volume] = yield channel.getMuteAndVolume(this.socket, [
                                'poll',
                            ]);
                            if (channel.isMuteConfigured) {
                                this.state.mutes.update(channel.key, mute);
                            }
                            if (channel.isVolumeConfigured) {
                                this.state.volumes.update(channel.key, volume);
                            }
                        }
                        this.channelPollIndex++;
                        if (this.channelPollIndex >= this.configuredChannels.length) {
                            this.channelPollIndex = 0;
                        }
                    }
                    else {
                        // If no channels configured, query the status of channel 1 as a dummy poll to
                        // prevent an idle timeout.
                        yield this.socket.send(makeCommand([0x00, 0x01, 0x00]), /^\xF0\x00\x01\x2a\x06\x00\x01\x01\x00[\s\S]{149}/, ['poll']);
                    }
                }),
            },
        ];
        this.channels = new AshlyChannels(0, 0, 0, log); // placeholder
    }
    // phase callbacks
    onConfigure(config) {
        return __awaiter(this, void 0, void 0, function* () {
            // Determine number of expansion cards.
            const hardwareConfiguration = yield this.socket.send(makeCommand([0x00, 0x00, 0x00]), /\xF0\x00\x01\x2a\x06\x00\x01\x00[\s\S]{20}([\s\S]{2})[\s\S]{2}/, ['configure']);
            const [ins, outs] = this.determineInputsOutputs(hardwareConfiguration[1]);
            if (debug)
                this.log.debug(`ins and outs = ${[ins, outs]}`);
            // Create the set of channels based on the expansion cards. 32 presets are supported in the
            // device according to the manual.
            this.channels = new AshlyChannels(ins, outs, 32, this.log);
            // Update channel names from the device.
            yield this.channels.updateNames(this.socket);
            if (debug)
                this.log.debug(`channels = ${JSON.stringify(this.channels, null, 4)}`);
        });
    }
    onControl() {
        return __awaiter(this, void 0, void 0, function* () {
            this.configuredChannels.length = 0;
            this.channelPollIndex = 0;
            // Get configured channels for the mute and volume for polling.
            this.configuredChannels.push(...this.channels.getAllConfiguredChannels());
            if (debug) {
                this.log.debug(`configured Channels = ${JSON.stringify(this.configuredChannels, null, 4)}`);
            }
        });
    }
    onInitialize(credentials) {
        return __awaiter(this, void 0, void 0, function* () {
            const result = yield CommandSocketInitializer.initialize(this.socket, [
                {
                    errorCode: 'verifyFailed',
                    send: makeCommand([0x00, 0x01, 0x00]),
                    succeed: /^\xF0\x00\x01\x2a\x06\x00\x01\x01\x00[\s\S]{149}/, // Response for channel 1 (index = 0)
                },
            ], credentials);
            return result;
        });
    }
    // other class methods
    // Determine the number and type of expansion cards in the device. From the API, the expansion
    // cards are detected and can be either input or output cards.
    //
    // The two bytes of information are described below:
    //
    // byte 0: 8 7 6  5           4                3           2                1           0
    //         x x x  Exp1        Exp1             Exp2        Exp2             Exp3        Exp3
    //                present: 1  type: 1 (input)  present: 1  type: 1 (input)  present: 1  type: 1 (input)
    //                                  0 (output)                   0 (output)                   0 (output)
    //
    // byte 1: 8 7 6  5           4                x           x                 1             0
    //         x x x  Exp4        Exp4             0           0                 front switch  switch type
    //                present: 1  type: 1 (input)                                enabled: 0    device id: 0
    //                                  0 (output)
    determineInputsOutputs(equip) {
        // The baseline configuration includes 4 ins and outs.
        let numInputs = 4;
        let numOutputs = 4;
        const equip0 = equip.charCodeAt(0);
        const equip1 = equip.charCodeAt(1);
        // Parse the first byte.
        for (let bit = 5; bit > 0; bit -= 2) {
            const cardPresent = (equip0 >> bit) & 0x01;
            if (cardPresent) {
                const cardType = (equip0 >> (bit - 1)) & 0x01;
                if (cardType === 1) {
                    numInputs += 4;
                }
                else {
                    numOutputs += 4;
                }
            }
        }
        // Parse the second byte
        const cardPresent = (equip1 >> 5) & 0x01;
        if (cardPresent) {
            const cardType = (equip1 >> 4) & 0x01;
            if (cardType === 1) {
                numInputs += 4;
            }
            else {
                numOutputs += 4;
            }
        }
        return [numInputs, numOutputs];
    }
    getKey(config) {
        if (isAshlyPresetMuteConfig(config))
            return config.muteStatus;
        if (isAshlyAudioChannelConfig(config))
            return config;
        throw new Error(`invalid config: ${JSON.stringify(config)}`);
    }
    hasRoomVolumeMute() {
        if (this.volumeConfig) {
            const chan = this.channels.getByKey(this.volumeConfig);
            return (chan === null || chan === void 0 ? void 0 : chan.type) ? allowedMuteTypes.includes(chan.type) : false;
        }
        else {
            return false;
        }
    }
    syncVolumeMute(channel, volume) {
        return __awaiter(this, void 0, void 0, function* () {
            if (this.volumeConfig === undefined || !this.hasRoomVolumeMute())
                return;
            const key = this.getKey(this.volumeConfig);
            const newVolumeMute = volume <= -38;
            if (channel.key !== key)
                return;
            if (this.state.mutes.values[key] !== newVolumeMute) {
                const value = yield this.mute.set(key, newVolumeMute, ['set.volumeMute']);
                this.state.mutes.update(key, value);
            }
        });
    }
    validate(config, info) {
        return __awaiter(this, void 0, void 0, function* () {
            const key = this.getKey(config);
            if (debug) {
                this.log.debug(`inside validate for key: ${key}, config: ${JSON.stringify(config)}, info: ${JSON.stringify(info)}`);
            }
            if (isAshlyPresetMuteConfig(config)) {
                const muteStatusChannel = this.channels.getByKey(config.muteStatus);
                const mutePresetChannel = this.channels.getByKey(config.mutePreset);
                const unmutePresetChannel = this.channels.getByKey(config.unmutePreset);
                if (muteStatusChannel === undefined ||
                    mutePresetChannel === undefined ||
                    unmutePresetChannel === undefined) {
                    return 'missing';
                }
                if (muteStatusChannel.type === 'preset' ||
                    mutePresetChannel.type !== 'preset' ||
                    unmutePresetChannel.type !== 'preset') {
                    return 'incompatible';
                }
                // Configure the mute and unmute presets.
                if (muteStatusChannel) {
                    muteStatusChannel.isMuteConfigured = true;
                    muteStatusChannel.presetMute = {
                        mute: mutePresetChannel,
                        unmute: unmutePresetChannel,
                    };
                }
                return 'ok';
            }
            else if (isAshlyAudioChannelConfig(config)) {
                if (info.type === 'volume' && info.filter === 'amplifier')
                    this.volumeConfig = config;
                const channel = this.channels.getByKey(key);
                if (channel === undefined)
                    return 'missing';
                if (channel.type === 'preset')
                    return 'incompatible';
                if (info.type === 'volume') {
                    channel.isVolumeConfigured = true;
                    // Check if Volume mute can be supported by the channel.
                    if (config === this.volumeConfig && allowedMuteTypes.includes(channel.type)) {
                        channel.isMuteConfigured = true;
                    }
                }
                else if (info.type === 'mute') {
                    if (allowedMuteTypes.includes(channel.type))
                        channel.isMuteConfigured = true;
                }
                if (debug)
                    this.log.debug(`validChannel: ${JSON.stringify(config, null, 4)}`);
                return 'ok';
            }
            this.log.error(`invalid config: ${JSON.stringify(config)}`);
            return 'incompatible';
        });
    }
}
// static properties
AshlyNeDsp.channelsConfigName = 'ashlyNeDspControlChannels';
AshlyNeDsp.driverType = 'socketDriver';
AshlyNeDsp.key = '-Ne3aaBFpBJLbhJKTuJy';
AshlyNeDsp.socketOptions = [
    {
        commandExtractor: {
            terminators: ['\xF7'],
        },
        port: 4999,
        sendTerminator: '\xF7',
        isBinary: true,
    },
];
